'use client';

import { useEffect, useState } from 'react';

/**
 * Whether the viewer has asked their operating system for reduced motion.
 *
 * This is the one signal a seller's page design cannot override: a seller can
 * switch the starfield on for every visitor to their page, which takes the
 * site toggle away from a motion-sensitive visitor, but not this, because it is
 * not a site preference at all. The cosmic-effects gate, the starfield and the
 * add-to-cart flight all read it.
 *
 * False on the server and on the first client render, so both produce the
 * same markup; the real value lands in the mount effect. Every consumer is an
 * effect that is already off until mount (see resolveCosmicEffects), so that
 * first false never lets anything animate. Tracked live, so changing the OS
 * setting with the page open is honoured without a reload.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    // `addEventListener` on a MediaQueryList is missing on older Safari.
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return reduced;
}
